import { motion, AnimatePresence } from 'framer-motion';
import { ChevronRight, ArrowDown, ArrowUp } from 'lucide-react';
import { LESSON } from '@/constants/testIds';

const RecursionVisualization = ({ step, lessonId }) => {
  if (!step) return null;

  const { callStack = [], currentCall, returning, pegs } = step;

  return (
    <div className="space-y-6">
      {/* Tower of Hanoi Peg View */}
      {lessonId === 'tower-of-hanoi' && pegs && (
        <div className="flex justify-around items-end gap-4 h-48 bg-[#0F0F12] border border-white/10 rounded-lg p-4">
          {['A', 'B', 'C'].map(pegName => (
            <div key={pegName} className="flex flex-col items-center gap-1 h-full justify-end relative">
              <div className="absolute top-0 bottom-8 w-1 bg-[#71717A]/50" />
              <div className="flex flex-col-reverse gap-1 relative z-10">
                {pegs[pegName].map((disk, idx) => (
                  <motion.div
                    key={`${pegName}-${disk}`}
                    layout
                    initial={{ scale: 0.5, opacity: 0 }}
                    animate={{ scale: 1, opacity: 1 }}
                    transition={{ type: 'spring', stiffness: 200, damping: 20 }}
                    className="h-6 rounded flex items-center justify-center text-white text-xs font-code font-bold shadow-lg"
                    style={{
                      width: `${20 + disk * 20}px`,
                      background: `linear-gradient(to right, #FFB800, #FF8800)`
                    }}
                  >
                    {disk}
                  </motion.div>
                ))}
              </div>
              <div className="w-24 h-2 bg-[#71717A] rounded" />
              <div className="text-[#FFB800] font-code font-bold text-lg">{pegName}</div>
            </div>
          ))}
        </div>
      )}

      {/* Call Stack Visualization */}
      <div className="space-y-3">
        <h4 className="text-white font-heading font-semibold text-sm flex items-center gap-2">
          <ArrowDown className="w-4 h-4 text-[#FFB800]" />
          Call Stack ({callStack.length})
        </h4>
        {callStack.length === 0 ? (
          <div className="text-[#71717A] text-sm font-body italic text-center py-4">
            Stack is empty
          </div>
        ) : (
          <div className="space-y-2 max-h-60 overflow-y-auto">
            <AnimatePresence>
              {callStack.slice().reverse().map((call, idx) => {
                const originalIndex = callStack.length - 1 - idx;
                const isCurrent = originalIndex === currentCall;
                return (
                  <motion.div
                    key={`${call.func}-${call.param}-${originalIndex}`}
                    data-testid={`call-frame-${originalIndex}`}
                    initial={{ opacity: 0, x: -20 }}
                    animate={{ opacity: 1, x: 0 }}
                    exit={{ opacity: 0, x: 20 }}
                    className={`flex items-center justify-between px-4 py-2 rounded-lg border-2 font-code text-sm ${
                      isCurrent && returning
                        ? 'border-green-500 bg-green-500/10 text-green-400 shadow-[0_0_15px_rgba(34,197,94,0.3)]'
                        : isCurrent
                        ? 'border-[#FFB800] bg-[#FFB800]/10 text-[#FFB800] shadow-[0_0_15px_rgba(255,184,0,0.3)]'
                        : 'border-white/10 bg-[#1A1A20] text-[#A1A1AA]'
                    }`}
                  >
                    <div className="flex items-center gap-2">
                      <ChevronRight className="w-3 h-3" />
                      <span>{call.func}({call.param})</span>
                    </div>
                    {call.returnVal !== null && (
                      <div className="flex items-center gap-1 text-green-400">
                        <ArrowUp className="w-3 h-3" />
                        <span className="font-bold">{call.returnVal}</span>
                      </div>
                    )}
                  </motion.div>
                );
              })}
            </AnimatePresence>
          </div>
        )}
      </div>

      {/* Memoization Display for Fibonacci */}
      {lessonId === 'fibonacci' && step.memo && Object.keys(step.memo).length > 0 && (
        <div className="bg-[#0F0F12] border border-[#007AFF]/20 rounded-lg p-3">
          <div className="text-[#007AFF] text-xs font-code mb-2">MEMO CACHE</div>
          <div className="flex flex-wrap gap-2">
            {Object.entries(step.memo).map(([key, val]) => (
              <div key={key} className="bg-[#1A1A20] border border-[#007AFF]/30 rounded px-2 py-1 text-xs font-code">
                <span className="text-[#007AFF]">fib({key})</span>
                <span className="text-white ml-1">= {val}</span>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Move Counter for Tower of Hanoi */}
      {lessonId === 'tower-of-hanoi' && step.moveCount !== undefined && (
        <div className="bg-gradient-to-r from-[#FFB800]/10 to-[#FF8800]/10 border border-[#FFB800]/30 rounded-lg p-4 text-center">
          <div className="text-[#A1A1AA] text-sm font-body mb-1">Total Moves</div>
          <div className="text-[#FFB800] text-3xl font-code font-bold">{step.moveCount}</div>
        </div>
      )}

      {/* Final Result */}
      {step.result !== undefined && (
        <div className="bg-gradient-to-r from-green-500/10 to-green-400/10 border border-green-500/30 rounded-lg p-4 text-center">
          <div className="text-[#A1A1AA] text-sm font-body mb-1">Final Result</div>
          <div className="text-green-400 text-3xl font-code font-bold">{step.result}</div>
        </div>
      )}
    </div>
  );
};

export default RecursionVisualization;
